import React, { useState, useEffect } from 'react';
import { useSOWMessagesStore, useTranslation, useSOWDataStore } from '../../store';
import Loading from '../Loading';
import LoadingError from '../LoadingError';
import NoData from '../NoData';
import '../../css/Messages.css';
import { queryParamsService } from '../../services/queryParams';

const MessagesTab = () => {
  const { 
    sowMessages, 
    sowMessagesLoading, 
    sowMessagesError, 
    fetchSOWMessages,
    setSOWMessages,
    createSOWMessage 
  } = useSOWMessagesStore();
  
  const [newMessage, setNewMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sortOrder, setSortOrder] = useState('newest');
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation();
  const { getSOWName, sowData } = useSOWDataStore();

  // Fetch messages on component mount
  useEffect(() => {
    queryParamsService.initialize();
    // The store records the failure in sowMessagesError, which the tab renders
    const loadMessages = async () => {
      try {
        await fetchSOWMessages(true);
      } catch (error) {
        console.error('Failed to load messages:', error);
      }
    };
    loadMessages();
  }, []);

  // Add new message function
  const addNewMessage = async () => {
    if (!newMessage.trim()) return;

    setIsSubmitting(true);
    
    try {
      await createSOWMessage(newMessage.trim(), t);
      setNewMessage('');
      
    } catch (error) {
      console.error('Failed to add message:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Filter and sort messages
  const filteredAndSortedMessages = React.useMemo(() => {
    if (!sowMessages) return [];
    
    let messages = [...sowMessages];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      messages = messages.filter(message => 
        message.body.toLowerCase().includes(term) ||
        message.author.toLowerCase().includes(term)
      );
    }
    
    // Apply sort
    messages.sort((a, b) => {
      return sortOrder === 'newest' ? b.created - a.created : a.created - b.created;
    });
    
    return messages;
  }, [sowMessages, searchTerm, sortOrder]);

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const formatRelativeTime = (timestamp) => {
    const now = Date.now();
    const diff = now - timestamp;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return t('justNow');
    if (minutes < 60) return t('minutesAgo', {'minutes': minutes});
    if (hours < 24) return t('hoursAgo', {'hours': hours});
    if (days < 7) return t('daysAgo', {'days': days});
    
    return new Date(timestamp).toLocaleDateString();
  };

  if (sowMessagesLoading) {
    return (
        <Loading message={t('loadingMessages')}/>
    );
  }

  if (sowMessagesError) {
    return (
        <LoadingError message={t('errorLoadingMessages', {'error': sowMessagesError})}/>
    );
  }
  
  if (!sowData) {
    return (
      <NoData/>
    );
  }

  return (
    <div className="tab-content messages-tab">
      <div className="messages-header">
        <h2>{t('workOrderCommunications')}</h2>
        <p>{t('messagedForWorkorder')} {getSOWName()}</p>
      </div>

      {/* Controls */}
      <div className="messages-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('searchMessages')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="sort-controls">
          <label>{t('sortBy')}</label>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
            className="sort-select"
          >
            <option value="newest">{t('newestFirst')}</option>
            <option value="oldest">{t('oldestFirst')}</option>
          </select>
        </div>
      </div>

      {/* Statistics */}
      <div className="messages-stats">
        <div className="stat">
          <span className="stat-number">{sowMessages?.length || 0}</span>
          <span className="stat-label">{t('totalMessages')}</span>
        </div>
        <div className="stat">
          <span className="stat-number">
            {sowMessages?.filter(m => m.author !== 'JIRAUSER10108' && m.author !== 'admin').length || 0}
          </span>
          <span className="stat-label">{t('userMessages')}</span>
        </div>
        <div className="stat">
          <span className="stat-number">
            {sowMessages?.filter(m => m.author === 'JIRAUSER10108' || m.author === 'admin').length || 0}
          </span>
          <span className="stat-label">{t('systemMessages')}</span>
        </div>
      </div>

      {/* Add New Message */}
      <div className="new-message-section">
        <h3>{t('addNewMessage')}</h3>
        <div className="message-input-container">
          <textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder={t('typeMessageHerePlaceholder')}
            rows={3}
            className="message-input"
            disabled={isSubmitting}
          />
          <div className="message-actions">
            <button
              onClick={addNewMessage}
              disabled={isSubmitting || !newMessage.trim()}
              className="send-message-btn"
            >
              {isSubmitting ? (
                <>
                  <span className="spinner"></span>
                  {t('sending')}
                </>
              ) : (
                t('sendMessage')
              )}
            </button>
            <button
              onClick={() => setNewMessage('')}
              disabled={isSubmitting || !newMessage}
              className="clear-message-btn"
            >
              {t('clear')}
            </button>
          </div>
        </div>
      </div>

      {/* Messages List */}
      <div className="messages-list">
        <h3>{t('messageHistory')} ({filteredAndSortedMessages.length})</h3>
        
        {filteredAndSortedMessages.length === 0 ? (
          <div className="no-messages">
            {searchTerm ? t('noMessagesFound') : t('noMessagesYet')}
          </div>
        ) : (
          filteredAndSortedMessages.map((message) => (
            <div key={message.id} className={`message-item ${message.author.toLowerCase()}`}>
              <div className="message-header">
                <div className="message-author">
                  <span className="author-avatar">
                    {message.author.charAt(0).toUpperCase()}
                  </span>
                  <span className="author-name">{message.author}</span>
                </div>
                <div className="message-time">
                  <span className="absolute-time">{formatDate(message.created)}</span>
                  <span className="relative-time">{formatRelativeTime(message.created)}</span>
                </div>
              </div>
              
              <div className="message-content">
                {message.body}
              </div>
              
              <div className="message-footer">
                <span className="message-id">{t('id')} {message.id}</span>
                {message.created !== message.updated && (
                  <span className="edited-badge">{t('edited')}</span>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Refresh Button */}
      <div className="refresh-section">
        <button onClick={fetchSOWMessages} className="refresh-btn">
          <span className="glyphicon glyphicon-refresh"></span> {t('refreshMessages')}
        </button>
        <span className="last-updated">
          {sowMessages && sowMessages.length > 0 && (
            `${t('lastMessage')} ${formatRelativeTime(Math.max(...sowMessages.map(m => m.created)))}`
          )}
        </span>
      </div>
    </div>
  );
};

export default MessagesTab;